import { useEffect, useState } from "react";
import { encode } from "gpt-tokenizer";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logApi, LogLevel } from "utils/sentryLogger";

export const useSessionData = (teacherEmail, classroomId = null, { selectAll = false } = {}) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [minDurationOnly, setMinDurationOnly] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState({});
  const [analysisData, setAnalysisData] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState({});
  const [sessionsExpanded, setSessionsExpanded] = useState(true);

  const selectedSessionIds = Object.entries(selectedSessions)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const allSelected = sessions && sessions.length > 0 && sessions.every((s) => selectedSessions[s.session_id]);

  const allFieldsSelected = availableFields.length > 0 && availableFields.every((f) => selectedFields[f]);

  useEffect(() => {
    setSessions(null);
    setSelectedSessions({});
    setAnalysisData(null);
    setAvailableFields([]);
    setSelectedFields({});
    setFetchError(null);
  }, [classroomId]);

  const estimatedTokenCount = (() => {
    if (!analysisData || availableFields.length === 0) return 0;
    const includedFields = availableFields.filter((f) => selectedFields[f]);
    if (includedFields.length === 0) return 0;

    let totalTokens = 0;
    analysisData
      .filter((item) => selectedSessions[item.session_id])
      .forEach((item) => {
        if (!item.analysis || typeof item.analysis !== "object") return;
        const filtered = {};
        includedFields.forEach((f) => {
          if (f in item.analysis) filtered[f] = item.analysis[f];
        });
        totalTokens += encode(JSON.stringify(filtered)).length;
      });

    return totalTokens;
  })();

  const handleFetchSessions = async (e) => {
    e.preventDefault();
    setIsFetching(true);
    setFetchError(null);
    setSessions(null);
    setSelectedSessions({});
    setAnalysisData(null);

    try {
      const payload = {
        teacher_email: teacherEmail,
        start_date: startDate,
        end_date: endDate,
      };
      if (minDurationOnly) {
        payload.min_duration_minutes = 5.0;
      }
      if (classroomId) {
        payload.classroom_id = classroomId;
      }

      const response = await request("admin/custom-report/fetch-sessions", { payload });

      const sessionScore = (s) => (s.duration_minutes ?? 0) * (s.student_count ?? 0);
      const scoredSessions = [...(response.sessions || [])].sort(
        (a, b) => sessionScore(b) - sessionScore(a)
      );

      let rankedSessions;
      const initialSelected = {};

      if (selectAll) {
        rankedSessions = scoredSessions;
        rankedSessions.forEach((s) => { initialSelected[s.session_id] = true; });
      } else {
        // Sum student-minutes per standard to find the top standard
        const standardTotals = {};
        scoredSessions.forEach((s) => {
          const std = s.standard || "";
          if (!standardTotals[std]) standardTotals[std] = 0;
          standardTotals[std] += sessionScore(s);
        });
        const topStandard = Object.entries(standardTotals).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";

        // Top-standard sessions first (score desc), then others (score desc)
        rankedSessions = [
          ...scoredSessions.filter((s) => (s.standard || "") === topStandard),
          ...scoredSessions.filter((s) => (s.standard || "") !== topStandard),
        ];
        rankedSessions.forEach((s) => {
          initialSelected[s.session_id] = (s.standard || "") === topStandard;
        });
      }

      setSessions(rankedSessions);
      const aData = response.analysis_data || [];
      setAnalysisData(aData);
      setSelectedSessions(initialSelected);

      const EXCLUDED_FIELDS = ["participant_mapping"];
      const isExcluded = (key) =>
        EXCLUDED_FIELDS.some((f) => key.toLowerCase().replace(/^_/, "") === f);
      const fieldSet = new Set();
      aData.forEach((item) => {
        if (item.analysis && typeof item.analysis === "object") {
          Object.keys(item.analysis)
            .filter((key) => !isExcluded(key))
            .forEach((key) => fieldSet.add(key));
        }
      });
      const fields = Array.from(fieldSet).sort();
      setAvailableFields(fields);
      const initialFields = {};
      fields.forEach((f) => { initialFields[f] = true; });
      setSelectedFields(initialFields);
    } catch (e) {
      console.error(e);
      logApi("Custom report fetch sessions failed", {
        level: LogLevel.WARNING,
        endpoint: "admin/custom-report/fetch-sessions",
        statusCode: e?.status,
        errorMessage: e?.detail || e?.message,
      });
      const detail = e?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : typeof detail === "string"
        ? detail
        : e?.message || "Failed to fetch sessions.";
      setFetchError(errorMsg);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSelectAll = (checked) => {
    const updated = {};
    (sessions || []).forEach((s) => {
      updated[s.session_id] = checked;
    });
    setSelectedSessions(updated);
  };

  const handleToggleSession = (sessionId) => {
    setSelectedSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  const handleToggleField = (field) => {
    setSelectedFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSelectAllFields = (checked) => {
    const updated = {};
    availableFields.forEach((f) => { updated[f] = checked; });
    setSelectedFields(updated);
  };

  return {
    startDate, setStartDate,
    endDate, setEndDate,
    minDurationOnly, setMinDurationOnly,
    isFetching, fetchError,
    sessions, selectedSessions, selectedSessionIds, allSelected,
    analysisData, availableFields, selectedFields, allFieldsSelected,
    sessionsExpanded, setSessionsExpanded,
    estimatedTokenCount,
    handleFetchSessions, handleSelectAll, handleToggleSession,
    handleToggleField, handleSelectAllFields,
  };
};
